"use client";

import React, { useEffect, useRef } from "react";
import { InputField } from "@ospweb/sdk-ui";
import { cn } from "utils";

import { createInviteCodeCopy } from "./inviteCodeCopy";

/**
 * 邀请码输入框 —— Figma `104980:320357`（默认）/ `104980:320418`（错误）。
 *
 * 容器样式与 `EmailLogin/shared/EmailInputField` 的 `mobile` variant 逐字对齐
 * （同一个 Figma library 组件 `Input Field`）：`rounded-[10px] border bg-…primary px-3 py-[14px]`。
 * 错误态：边框转 `border/accent2/transparent50%`，下方 8px 处出 helper text（`body_s`）。
 */
interface InviteCodeInputFieldProps {
  value: string;
  onValueChange: (value: string) => void;
  /** 后端不认这个码 / 格式非法 → 显示错误边框与 helper text。 */
  error?: boolean;
  /** 覆盖默认错误文案（默认取 `We don't recognise that code…`）。 */
  errorMessage?: string;
  locale?: string;
  className?: string;
}

export function InviteCodeInputField({
  value,
  onValueChange,
  error = false,
  errorMessage,
  locale,
  className,
}: InviteCodeInputFieldProps) {
  const inputRef = useRef<React.ComponentRef<typeof InputField>>(null);
  const copy = createInviteCodeCopy(locale);

  useEffect(() => {
    inputRef.current?.setError?.(error);
  }, [error]);

  return (
    <div className={cn("w-full", error && "flex flex-col gap-2", className)}>
      <InputField
        ref={inputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={copy.placeholder}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        size="small"
        containerClassName="flex min-h-12 w-full items-center rounded-[10px] border border-solid border-border_solid bg-background_solid_neutral_primary px-3 py-[14px]"
        inputClassName="min-w-0 flex-1 border-0 bg-transparent outline-none body_m text-content_primary placeholder:text-content_quaternary"
        aria-invalid={error}
        aria-describedby={error ? "invite-code-error" : undefined}
        data-testid="invite-code-input"
      />
      {error && (
        <p
          id="invite-code-error"
          className="body_s shrink-0 px-1 text-content_accent2_primary"
          role="alert"
          data-testid="invite-code-error"
        >
          {errorMessage ?? copy.invalidCode}
        </p>
      )}
    </div>
  );
}
